import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Textarea } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { rolesApi } from '../../api/roles.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import type { Role } from '../../api/types'
import formStyles from '../../components/ui/Form.module.css'

export function RoleFormModal({ role, onClose }: { role?: Role | null; onClose: () => void }) {
  const isEdit = Boolean(role)
  const isSystem = role?.isSystem ?? false
  const queryClient = useQueryClient()

  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [color, setColor] = useState(role?.color ?? '#6366f1')
  const [permissionKeys, setPermissionKeys] = useState<string[]>(role?.permissions.map((p) => p.permissionKey) ?? [])
  const [error, setError] = useState<string | null>(null)

  const catalogQuery = useQuery({ queryKey: ['permission-catalog'], queryFn: async () => (await rolesApi.permissionCatalog()).data })

  const grouped = useMemo(() => {
    const groups: Record<string, { key: string; description: string }[]> = {}
    for (const entry of catalogQuery.data ?? []) {
      const [module] = entry.key.split('.')
      const groupKey = module ?? entry.key
      groups[groupKey] ??= []
      groups[groupKey].push(entry)
    }
    return groups
  }, [catalogQuery.data])

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit && role) {
        await rolesApi.update(role.id, { description, color, permissionKeys: isSystem ? undefined : permissionKeys })
      } else {
        await rolesApi.create({ name, description, color, permissionKeys })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success(isEdit ? 'Rolle aktualisiert.' : 'Rolle angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  const togglePermission = (key: string) => {
    setPermissionKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  return (
    <Modal
      title={isEdit ? `Rolle bearbeiten — ${role?.name}` : 'Neue Rolle'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        {isSystem ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
            Die System-Rolle "Administrator" behält immer alle Rechte und kann nicht umbenannt werden.
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Name" htmlFor="role-name">
              <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} required disabled={isSystem} />
            </Field>
          </div>
          <div style={{ width: 100 }}>
            <Field label="Farbe" htmlFor="role-color">
              <Input id="role-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ padding: 4 }} />
            </Field>
          </div>
        </div>

        <Field label="Beschreibung (optional)" htmlFor="role-description">
          <Textarea id="role-description" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Field label="Berechtigungen">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxHeight: 320, overflowY: 'auto' }}>
            {Object.entries(grouped).map(([group, entries]) => (
              <div key={group}>
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  {group}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {entries.map((entry) => (
                    <label key={entry.key} className={formStyles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={permissionKeys.includes(entry.key)}
                        disabled={isSystem}
                        onChange={() => togglePermission(entry.key)}
                      />
                      <span>{entry.description}</span>
                      <code style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{entry.key}</code>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Field>
      </form>
    </Modal>
  )
}
